// src/components/ProtectedRoute.js
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Loader = () => (
  <div
    style={{ padding: '2rem', textAlign: 'center' }}
    role="status"
    aria-live="polite"
  >
    Loading…
  </div>
);

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isLoggedIn, loading, user } = useAuth();
  const location = useLocation();

  if (loading) return <Loader />;

  // Not authenticated → send to login and remember where we were headed
  if (!isLoggedIn) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  // Normalize roles (allow string or array); case-insensitive compare
  const normalizedAllowed =
    allowedRoles == null
      ? null
      : (Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles])
          .map(r => String(r).toLowerCase());

  const userRole = String(user?.role || '').toLowerCase();
  const authorized =
    !normalizedAllowed || normalizedAllowed.includes(userRole);

  if (!authorized) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>⛔ Access Denied</h2>
        <p>You do not have permission to view this page.</p>
      </div>
    );
  }

  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  // Accepts 'admin' or ['admin','super-admin'], or omit to allow any logged-in user
  allowedRoles: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};

export default memo(ProtectedRoute);
